import React from 'react';
import { Award, Network, Users } from 'lucide-react';
import { Reveal } from './Reveal';
import { CaseCard, DivisionCard, OrchestrationBanner, PageEyebrow, TechModuleCard } from './cards';
import { CASES, DIVISIONS, TECH_MODULES, delayClass } from './data';

export function TechView() {
  return (
    <div className="pt-40 pb-24 min-h-screen relative overflow-hidden bg-[#060606]">
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-brand-dark/20 rounded-full blur-[150px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <Reveal>
          <PageEyebrow icon={Network} tone="emerald">
            Specialized Clinical Agents
          </PageEyebrow>
          <h1 className="text-5xl md:text-7xl font-semibold mb-6 text-white text-glow-premium tracking-tight">Purpose-Built Architecture.</h1>
          <p className="text-xl text-slate-400 max-w-3xl font-light mb-16 leading-relaxed">
            We don't use generic chatbots. We engineer specialized, multi-agent systems designed explicitly for medical evidence generation. Every output is benchmarked. Every step is transparent and explainable. No black boxes.
          </p>
        </Reveal>

        <Reveal delay="delay-150">
          <OrchestrationBanner />
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6">
          {TECH_MODULES.map((module, idx) => (
            <Reveal key={module.code} delay={delayClass((idx + 1) * 150)}>
              <TechModuleCard {...module} />
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}

export function TeamView() {
  return (
    <div className="pt-40 pb-24 min-h-screen relative overflow-hidden">
      <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-brand/20 rounded-full blur-[150px] pointer-events-none"></div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <Reveal>
          <PageEyebrow icon={Users} tone="blue">
            Vetted Expertise
          </PageEyebrow>
          <h1 className="text-5xl md:text-7xl font-semibold mb-6 text-white text-glow-premium tracking-tight">Clinical Intellect.</h1>
          <p className="text-xl text-slate-400 max-w-2xl font-light mb-16 leading-relaxed">We don't employ generalist copywriters. Your science is synthesized, structured, and defended exclusively by domain-specific MD and PhD experts.</p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6">
          {DIVISIONS.map((dept, i) => (
            <Reveal key={dept} delay={delayClass(i * 100)}>
              <DivisionCard name={dept} />
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CasesView() {
  return (
    <div className="pt-40 pb-24 min-h-screen relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-slate-800/20 rounded-full blur-[150px] pointer-events-none"></div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <Reveal>
          <PageEyebrow icon={Award} tone="slate">
            Case Studies
          </PageEyebrow>
          <h1 className="text-5xl md:text-7xl font-semibold mb-6 text-white text-glow-premium tracking-tight">Proven Impact.</h1>
          <p className="text-xl text-slate-400 max-w-2xl font-light mb-16 leading-relaxed">Tangible outcomes from leading clinical institutions and global MedTech manufacturers.</p>
        </Reveal>

        <div className="space-y-6">
          {CASES.map((caseItem, i) => (
            <Reveal key={caseItem.title} delay={delayClass(i * 150)}>
              <CaseCard {...caseItem} />
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
